
import React from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Copy, RefreshCw } from "lucide-react";
import { toast } from 'sonner';
import { debugLogger } from '@/utils/debugLogger';

interface OnboardingErrorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRetry: () => void;
  stepNumber: number;
  errorDetails: {
    message: string;
    timestamp: string;
    step: number;
    type: string;
  } | null;
}

export const OnboardingErrorModal: React.FC<OnboardingErrorModalProps> = ({
  isOpen,
  onClose,
  onRetry,
  stepNumber,
  errorDetails
}) => {
  // Only process if modal is actually open and errorDetails exists
  if (!isOpen || !errorDetails) {
    return null;
  }

  // Defensive null checks and fallback values
  const safeErrorDetails = {
    message: errorDetails.message || 'Erro desconhecido',
    timestamp: errorDetails.timestamp || new Date().toISOString(),
    step: errorDetails.step || stepNumber,
    type: errorDetails.type || 'unknown_error'
  };

  const copyErrorDetails = () => {
    const errorText = `
Erro no Onboarding - Passo ${stepNumber}
Timestamp: ${safeErrorDetails.timestamp}
Tipo: ${safeErrorDetails.type}
Mensagem: ${safeErrorDetails.message}
Passo: ${safeErrorDetails.step}

Por favor, envie estas informações para os administradores do site.
    `.trim();

    navigator.clipboard.writeText(errorText);
    toast.success('Detalhes do erro copiados para a área de transferência');
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={onClose}>
      <AlertDialogContent className="bg-black/90 border border-red-500/30 text-white max-w-md">
        <AlertDialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-full bg-red-500/20">
              <AlertTriangle className="h-6 w-6 text-red-400" />
            </div>
            <AlertDialogTitle className="text-white">
              Erro ao Salvar Dados
            </AlertDialogTitle>
          </div>
          <AlertDialogDescription className="text-white/80">
            Ocorreu um erro ao salvar os dados do passo {stepNumber} do seu onboarding. 
            Seus dados não foram perdidos, mas precisamos resolver este problema antes de continuar.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 my-4">
          <p className="text-red-400 text-sm font-medium mb-2">Detalhes do erro:</p>
          <p className="text-white/70 text-xs">{safeErrorDetails.message}</p>
          <p className="text-white/50 text-xs mt-1">
            Timestamp: {new Date(safeErrorDetails.timestamp).toLocaleString()}
          </p>
        </div>

        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 mb-4">
          <p className="text-yellow-400 text-sm font-medium mb-1">O que fazer agora:</p>
          <ul className="text-white/80 text-xs space-y-1">
            <li>• Tente novamente clicando em "Tentar Novamente"</li>
            <li>• Se o erro persistir, copie os detalhes e envie para os administradores</li>
            <li>• Seus dados anteriores estão seguros</li>
          </ul>
        </div>

        <AlertDialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={copyErrorDetails}
            className="bg-transparent border-white/20 text-white hover:bg-white/10"
          >
            <Copy className="h-4 w-4 mr-2" />
            Copiar Detalhes
          </Button>
          <AlertDialogCancel className="bg-gray-600 hover:bg-gray-700 text-white border-gray-500">
            Fechar
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={onRetry}
            className="bg-luxury-gold hover:bg-luxury-gold/90 text-black"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Tentar Novamente
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
